/**
 * PessulusLite — the main-thread half of warble-processor.js.
 *
 * Mirrors scratch/engine.ts: loads the worklet from a real module URL, exposes
 * typed setters, connects to the provided output node and never to
 * ctx.destination.
 *
 * The param discipline is Chirp's own (warble.html:913-935): CONTINUOUS params
 * (alpha, beta, fold, grit) move via setTargetAtTime with Chirp's time
 * constants — "four events, not ninety-six" — while STRUCTURAL params (gamma,
 * refl, spread) change via setValueAtTime on discrete acts. That split is also
 * this repo's invariant 4.
 */

export interface WarbleStructure {
  gamma?: number;
  refl?: number;
  spread?: number;
}

export interface Pessulus {
  readonly node: AudioWorkletNode;
  setAlpha(v: number): void;
  setBeta(v: number): void;
  setFold(v: number): void;
  setGrit(v: number): void;
  setStructure(s: WarbleStructure): void;
  /** Phonation gate: true eases env to 1 (6 ms), false to 0 (20 ms). */
  gate(on: boolean): void;
  /** The pinch's staccato attack: env snaps to 0 then eases back to 1. */
  rearticulate(): void;
  /** Diagnostics from the processor, every 8 render quanta. */
  onDiag(cb: (d: { period: number; guard: number }) => void): void;
  setLevel(v: number): void;
  dispose(): void;
}

let modulePromise: Promise<void> | null = null;

/** Idempotent: the module is added to a given context exactly once. */
export function ensureWarbleModule(ctx: BaseAudioContext, url = 'worklets/warble-processor.js'): Promise<void> {
  if (!modulePromise) modulePromise = ctx.audioWorklet.addModule(url);
  return modulePromise;
}

export async function createPessulus(
  ctx: AudioContext,
  out: AudioNode,
  opts: { level?: number; ovs?: 1 | 2 | 4; moduleUrl?: string } = {},
): Promise<Pessulus> {
  const { level = 0.8, ovs = 4, moduleUrl = 'worklets/warble-processor.js' } = opts;
  await ensureWarbleModule(ctx, moduleUrl);
  const node = new AudioWorkletNode(ctx, 'warble', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    processorOptions: { ovs },
  });
  const master = ctx.createGain();
  master.gain.value = level * level;
  node.connect(master);
  master.connect(out);

  const p = (name: string): AudioParam => node.parameters.get(name)!;
  let diagCb: ((d: { period: number; guard: number }) => void) | null = null;
  node.port.onmessage = (e) => {
    const m = e.data as { period?: number; guard?: number };
    if (diagCb && typeof m.guard === 'number') diagCb({ period: m.period ?? 0, guard: m.guard });
  };

  return {
    node,
    // Chirp's own time constants (warble.html:917-919).
    setAlpha(v) { p('alpha').setTargetAtTime(v, ctx.currentTime, 0.02); },
    setBeta(v) { p('beta').setTargetAtTime(v, ctx.currentTime, 0.014); },
    setFold(v) { p('fold').setTargetAtTime(v, ctx.currentTime, 0.03); },
    // k-rate: sampled once per block; the smoothing still shapes the walk.
    setGrit(v) { p('grit').setTargetAtTime(v, ctx.currentTime, 0.05); },
    setStructure(s) {
      const t = ctx.currentTime;
      if (s.gamma !== undefined) p('gamma').setValueAtTime(s.gamma, t);
      if (s.refl !== undefined) p('refl').setValueAtTime(s.refl, t);
      if (s.spread !== undefined) p('spread').setValueAtTime(s.spread, t);
    },
    gate(on) {
      const e = p('env'), t = ctx.currentTime;
      e.cancelScheduledValues(t);
      e.setTargetAtTime(on ? 1 : 0, t, on ? 0.006 : 0.02);
    },
    rearticulate() {
      const e = p('env'), t = ctx.currentTime;
      e.cancelScheduledValues(t);
      e.setValueAtTime(0, t);
      e.setTargetAtTime(1, t + 0.002, 0.006);
    },
    onDiag(cb) { diagCb = cb; },
    setLevel(v) { master.gain.setTargetAtTime(v * v, ctx.currentTime, 0.02); },
    dispose() {
      try { node.port.onmessage = null; node.disconnect(); master.disconnect(); } catch { /* gone */ }
    },
  };
}
